/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { BinaryFileListResource } from '@common/binary-file-shared';
import { CommandOrder, CommandResource, PendingCommandMap } from '@alfa-client/command-shared';
import { EMPTY_STRING, StateResource } from '@common/tech-shared';
import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import { ResourceUri } from '@ngxp/rest';
import { VorgangNavigationUtil } from '../vorgang-navigation.util';
import {
  StatusCommandMap,
  VorgangFilter,
  VorgangListResource,
  VorgangResource,
  VorgangStatistic,
  VorgangView,
  VorgangWithEingangResource,
} from '../vorgang.model';
import { VORGANG_FEATURE_KEY, VorgangState } from './vorgang.reducer';

const getVorgangState: MemoizedSelector<object, VorgangState> = createFeatureSelector<VorgangState>(VORGANG_FEATURE_KEY);

// VorgangList
const vorgangList: MemoizedSelector<VorgangState, StateResource<VorgangListResource>> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.vorgangList,
);

const vorgangListLast: MemoizedSelector<VorgangState, StateResource<VorgangListResource>> = createSelector(
  getVorgangState,
  (state: VorgangState) => (state.vorgangListNext.resource ? state.vorgangListNext : state.vorgangList),
);

const vorgangListNext: MemoizedSelector<VorgangState, StateResource<VorgangListResource>> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.vorgangListNext,
);

const vorgaenge: MemoizedSelector<VorgangState, VorgangResource[]> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.vorgaenge,
);
const vorgangStatistic: MemoizedSelector<VorgangState, StateResource<VorgangStatistic>> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.vorgangStatistic,
);

const vorgangFilter = createSelector(getVorgangState, (state: VorgangState) => state.vorgangFilter);
const vorgangView = createSelector(getVorgangState, (state: VorgangState) => state.vorgangView);

const searchPreviewList: MemoizedSelector<VorgangState, StateResource<VorgangListResource>> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.searchPreviewList,
);
const searchString: MemoizedSelector<VorgangState, string> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.searchString,
);

const backButtonUrl: MemoizedSelector<VorgangState, string> = createSelector(getVorgangState, (state: VorgangState) =>
  VorgangNavigationUtil.buildBackButtonUrl(state),
);

const isVorgangViewSelected = (view: VorgangView) =>
  createSelector(vorgangView, (vorgangViewInState: VorgangView) => vorgangViewInState === view);
const getVorgangViewRoutePath = (view: VorgangView) =>
  createSelector(vorgangFilter, (vorgangFilterInState: VorgangFilter) =>
    VorgangNavigationUtil.buildVorgangFilterViewRoutePath(vorgangFilterInState, view, EMPTY_STRING),
  );

const isVorgangFilterSelected = (filter: VorgangFilter) =>
  createSelector(vorgangFilter, (vorgangFilterInState: VorgangFilter) => vorgangFilterInState === filter);
const getVorgangFilterRoutePath = (filter: VorgangFilter) =>
  createSelector(vorgangView, searchString, (vorgangViewInState: VorgangView, searchStringInState: string) =>
    VorgangNavigationUtil.buildVorgangFilterViewRoutePath(filter, vorgangViewInState, searchStringInState),
  );

//VorgangWithEingang
const vorgangWithEingang: MemoizedSelector<VorgangState, StateResource<VorgangWithEingangResource>> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.vorgangWithEingang,
);
const attachmentList: MemoizedSelector<VorgangState, StateResource<BinaryFileListResource>> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.attachmentList,
);
const representationList: MemoizedSelector<VorgangState, StateResource<BinaryFileListResource>> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.representationList,
);
const forwardPendingCommand: MemoizedSelector<VorgangState, StateResource<CommandResource>> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.forwardPendingCommand,
);
const sendPostfachNachrichtPendingCommand: MemoizedSelector<VorgangState, StateResource<CommandResource>> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.sendPostfachNachrichtPendingCommand,
);

const assignUserCommand: MemoizedSelector<VorgangState, StateResource<CommandResource>> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.assignUserCommand,
);

const unassignUserCommand: MemoizedSelector<VorgangState, StateResource<CommandResource>> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.unassignUserCommand,
);

const statusCommandMap: MemoizedSelector<VorgangState, StatusCommandMap> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.statusCommandMap,
);
const getStatusCommand = (order: CommandOrder) =>
  createSelector(statusCommandMap, (statusCommandMapInState: StatusCommandMap) => statusCommandMapInState[order]);

const revokeCommand: MemoizedSelector<VorgangState, StateResource<CommandResource>> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.revokeCommand,
);

const vorgangExport: MemoizedSelector<VorgangState, StateResource<boolean>> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.vorgangExport,
);

const pendingCommandMap: MemoizedSelector<VorgangState, PendingCommandMap> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.pendingCommandMap,
);
const getPendingCommand = (uri: ResourceUri) =>
  createSelector(pendingCommandMap, (pendingCommandMapInState: PendingCommandMap) => pendingCommandMapInState[uri]);

const lastSelectedVorgangUri: MemoizedSelector<VorgangState, string> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.lastSelectedVorgang?.uri,
);

const lastSelectedVorgangPageToLoad: MemoizedSelector<VorgangState, number> = createSelector(
  getVorgangState,
  (state: VorgangState) => state.lastSelectedVorgang?.pageToLoad,
);

export const VorgangSelectors = {
  vorgangList,
  vorgangListLast,
  vorgangListNext,
  vorgaenge,
  vorgangStatistic,
  vorgangFilter,
  vorgangView,
  searchPreviewList,
  searchString,
  backButtonUrl,
  isVorgangViewSelected,
  getVorgangViewRoutePath,
  isVorgangFilterSelected,
  getVorgangFilterRoutePath,
  vorgangWithEingang,
  attachmentList,
  representationList,
  forwardPendingCommand,
  sendPostfachNachrichtPendingCommand,
  assignUserCommand,
  unassignUserCommand,
  statusCommandMap,
  getStatusCommand,
  revokeCommand,
  vorgangExport,
  pendingCommandMap,
  getPendingCommand,
  lastSelectedVorgangUri,
  lastSelectedVorgangPageToLoad,
};
